import React from "react";
import { cssForWord } from "../../utilities/exportCSS";
import { dateParse, cleanLink } from './utils';

/**
 * HTMLReportTemplate renders a Word-compatible HTML document for the
 * "Generate HTML Document" functionality.
 *
 * Props contract:
 *   headerData        — { company, date, score, urlTested }
 *   params            — URLSearchParams (used for params.score fallback)
 *   showTable         — boolean; whether any table data exists
 *   testedURLsLinkList — pre-built link list from createLinkList()
 *   newData           — row data with guidanceHtml and notesHtml already processed
 *   generatedAt       — string timestamp for the document footer (new Date().toLocaleString())
 *
 * Data flow:
 *   - guidanceHtml: pre-processed via buildGuidanceHtml() (markdown → HTML, code spans
 *     → <p class="code">, bare tags entity-encoded). Injected with dangerouslySetInnerHTML.
 *   - notesHtml: pre-processed via buildNotesHtml() (backtick spans → <p class="code">,
 *     entity-encoded, \n → <br/>). Injected with dangerouslySetInnerHTML.
 *   - URL links: resolved inline from testedURLsLinkList, same as web template.
 *
 * Layout: one flat table with four columns — Accessibility Indicator | Guidance |
 * URL Tested & Notes | Capability. No per-criteria split (unlike PDF template).
 * The edit/actions column and "Modify this test" link are intentionally omitted.
 */
export default function HTMLReportTemplate(props) {
  const {
    headerData,
    params,
    showTable,
    testedURLsLinkList,
    newData,
    generatedAt,
  } = props;

  return (
    <html
      xmlns="http://www.w3.org/TR/REC-html40"
      xmlnsO="urn:schemas-microsoft-com:office:office"
      xmlnsW="urn:schemas-microsoft-com:office:word"
    >
      <head>
        <meta charSet="utf-8" />
        <title>{`${headerData.company} Accessibility Report`}</title>
        <meta name="ProgId" content="Word.Document" />
        <meta name="Generator" content="Microsoft Word 15" />
        <meta name="Originator" content="Microsoft Word 15" />
        <style dangerouslySetInnerHTML={{ __html: cssForWord.data }} />
        {/* Word-specific XML settings for print layout */}
        <xml>
          {`<w:WordDocument>
            <w:View>Print</w:View>
            <w:Zoom>100</w:Zoom>
            <w:DoNotOptimizeForBrowser/>
          </w:WordDocument>`}
        </xml>
      </head>
      <body>
        {/* ── Report header ─────────────────────────────────────────────── */}
        <div id="topReportInfo">
          <h1 className="page-header">{`${headerData.company} Accessibility Report`}</h1>
          <p id="scoreh2">{`Score: ${(params.score || headerData.score).toFixed(0)}%`}</p>
          <p id="dateLbl">{`Test Completion Date: ${dateParse(headerData.date).replaceAll(".", "")}`}</p>
          <p>{`Domains Tested: ${headerData.urlTested}`}</p>
        </div>

        {/* ── Tested links list ──────────────────────────────────────────── */}
        {showTable && (
          <div className="link-list">
            <h2>List of Links Tested</h2>
            <p>This list contains the pages that were tested and used in the report below.</p>
            <ul>
              {testedURLsLinkList.map((link, idx) => (
                <li key={`link-list-${idx}`}>
                  {link.title}: <a href={link.url}>{link.url}</a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ── Main report table ──────────────────────────────────────────── */}
        {showTable ? (
          <div className="Section1">
            <table id="rtable" aria-label="Accessibility Report">
              <thead id="reportHead">
                <tr>
                  <th id="accessibilityCol" scope="col">Accessibility Indicator</th>
                  <th id="guidanceCol" scope="col">Guidance</th>
                  <th id="urlCol" scope="col">URL Tested &amp; Notes</th>
                  <th id="capabilityCol" scope="col">Capability</th>
                </tr>
              </thead>
              <tbody id="reportBody">
                {newData.length > 0 && newData.map((data, idx) => {
                  // Resolve page links for this row from the stable link list.
                  const rowLinks = testedURLsLinkList.filter(
                    link => link.criteriaId.includes(data.criteriaid)
                  );

                  return (
                    <tr key={`row-${idx}`}>
                      {/* Accessibility Indicator */}
                      <td scope="row" className="reportCells accessibility">
                        <p><b>{data.criteria}</b></p>
                      </td>

                      {/* Guidance — pre-processed HTML */}
                      <td
                        className="reportCells guidance"
                        dangerouslySetInnerHTML={{ __html: data.guidanceHtml }}
                      />

                      {/* URL Tested & Notes — combined cell */}
                      <td className="reportCells urlNotes">
                        {rowLinks.length > 0 ? (
                          <ul>
                            {rowLinks.map((link) => (
                              <li key={link.url}>
                                <a href={link.url}>{link.title}</a>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          // Fallback: URL field has no http links — render as plain text.
                          data.url && <p>{data.url.replaceAll("\\n", "\n")}</p>
                        )}
                        {/* Notes — pre-processed HTML (backtick spans → code style) */}
                        <div
                          className="urlNotes-notes"
                          dangerouslySetInnerHTML={{ __html: data.notesHtml }}
                        />
                      </td>

                      {/* Capability */}
                      <td className="reportCells" id="capabilityCol">
                        {data.capability}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <h2>There is no feedback to display for this account.</h2>
        )}

        {/* ── Document footer ────────────────────────────────────────────── */}
        {generatedAt && (
          <p id="doc-footer">{`Generated: ${generatedAt}`}</p>
        )}
      </body>
    </html>
  );
}
